import { createAsyncThunk, createSlice, } from "@reduxjs/toolkit";
import { getCredits } from "../../api/credits/getCredits";
import { DAILY_BUCKET, SIGNUP_BONUS_BUCKET, TOP_UP_BUCKET, bucketRemaining, } from "./credits.types";
const initialState = {
    status: "idle",
    buckets: null,
    total: null,
    featureCosts: {},
    bucketsStale: false,
    paidSbcIds: [],
};
export const fetchCredits = createAsyncThunk("permissions/credits", () => getCredits());
/**
 * Draws `spent` down the buckets in the backend's order — the sign-up bonus
 * first, then the daily allowance, and purchased top-ups last, so a user always
 * burns what they were given before what they paid for (DEV-3210). Returns
 * false when the split can't be reconciled (no buckets loaded, unlimited daily,
 * or more spent than the buckets hold), which marks them stale for a refetch.
 */
const applySpendToBuckets = (buckets, spent) => {
    if (!buckets || spent <= 0)
        return false;
    let outstanding = spent;
    const drain = (bucket) => {
        if (!bucket || outstanding <= 0)
            return;
        const remaining = bucketRemaining(bucket);
        if (remaining <= 0)
            return;
        const taken = Math.min(remaining, outstanding);
        outstanding -= taken;
        // A positive remaining rules out the unlimited daily bucket, the only
        // member of the union without a numeric `remaining`.
        bucket.remaining = remaining - taken;
    };
    drain(buckets[SIGNUP_BONUS_BUCKET]);
    drain(buckets[DAILY_BUCKET]);
    drain(buckets[TOP_UP_BUCKET]);
    // Anything the backend adds that we don't know about still absorbs spend, so
    // an unrecognised bucket doesn't force a refetch on every charge. Ordered
    // last because a guess about a new bucket is worth less than the three rules
    // above, which are known.
    for (const name of Object.keys(buckets)) {
        if (name === SIGNUP_BONUS_BUCKET ||
            name === DAILY_BUCKET ||
            name === TOP_UP_BUCKET)
            continue;
        drain(buckets[name]);
    }
    return outstanding === 0;
};
const permissionsSlice = createSlice({
    name: "permissions",
    initialState,
    reducers: {
        /**
         * Applied from the `X-Credits-Left` / `X-Credit-Charged` headers that every
         * metered route returns. This is the primary sync path — a spend does not
         * trigger a refetch.
         */
        creditsHeaderReceived(state, action) {
            const { left, charged } = action.payload;
            const previousTotal = state.total;
            // Responses can land out of order, and headers only ever report a spend
            // (grants arrive through fetchCredits), so a balance above the one we
            // hold is a late response rather than news.
            if (typeof previousTotal === "number" &&
                typeof left === "number" &&
                left > previousTotal)
                return;
            state.total = left;
            if (charged) {
                state.lastChargedAt = Date.now();
                /*
                  The header carries only the aggregate, but the per-bucket split is
                  what the UI renders — the nav chip shows the daily count for a user
                  with no top-ups, and an unspent sign-up bonus hides the credit UI
                  entirely. Deriving the spend from the total delta and draining in the
                  backend's own order keeps the split right without a refetch.
                */
                const spent = typeof previousTotal === "number" && typeof left === "number"
                    ? previousTotal - left
                    : 0;
                state.bucketsStale = !applySpendToBuckets(state.buckets, spent);
            }
            // A balance arriving from headers before the first /credits response is
            // still usable for access checks, so leave "idle" behind.
            if (state.status === "idle")
                state.status = "fulfilled";
        },
        /** Backend rejected the call with 402 INSUFFICIENT_CREDITS. */
        creditsExhausted(state) {
            if (state.total !== "unlimited")
                state.total = 0;
            state.bucketsStale = true;
        },
        resetCredits: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCredits.pending, (state) => {
            state.status = "pending";
        })
            .addCase(fetchCredits.fulfilled, (state, action) => {
            state.status = "fulfilled";
            state.buckets = action.payload.buckets;
            state.total = action.payload.total;
            state.bucketsStale = false;
            state.paidSbcIds = action.payload.paidSbcIds ?? [];
            state.featureCosts = Object.fromEntries(Object.entries(action.payload.features ?? {}).map(([feature, price]) => [feature, price.cost]));
        })
            .addCase(fetchCredits.rejected, (state) => {
            state.status = "rejected";
        });
    },
});
export const { creditsHeaderReceived, creditsExhausted, resetCredits } = permissionsSlice.actions;
export default permissionsSlice.reducer;
//# sourceMappingURL=permissionsSlice.js.map